'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useAuth } from './useAuth'
import type { ReaderContent, ReaderSourceType } from '@/types/reader'

export type StoredReaderContent = {
  id: string
  user_id: string
  source_type: Exclude<ReaderSourceType, 'story'>
  title: string
  subtitle: string | null
  metadata: Record<string, unknown>
  content: ReaderContent
  processor_version: string
  dictionary_build_version: string | null
  progress: Record<string, unknown>
  last_opened_at: string | null
  created_at: string
  updated_at: string
}

export type StoredReaderContentSummary = Omit<StoredReaderContent, 'content'>

export function useReaderContents(
  sourceType?: Exclude<ReaderSourceType, 'story'>
) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['reader-contents', user?.id, sourceType],
    queryFn: async () => {
      if (!user?.id) return []

      let query = supabase
        .from('reader_contents')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })

      if (sourceType) {
        query = query.eq('source_type', sourceType)
      }

      const { data, error } = await query
      if (error) throw error

      return (data ?? []) as StoredReaderContent[]
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5
  })
}

export function useReaderContentSummaries(
  sourceType?: Exclude<ReaderSourceType, 'story'>
) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['reader-content-summaries', user?.id, sourceType],
    queryFn: async () => {
      if (!user?.id) return []

      let query = supabase
        .from('reader_contents')
        .select(
          'id,user_id,source_type,title,subtitle,metadata,processor_version,dictionary_build_version,progress,last_opened_at,created_at,updated_at'
        )
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })

      if (sourceType) {
        query = query.eq('source_type', sourceType)
      }

      const { data, error } = await query
      if (error) throw error

      return (data ?? []) as StoredReaderContentSummary[]
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5
  })
}

export function useReaderContent(id: string | null) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['reader-content', user?.id, id],
    queryFn: async () => {
      if (!user?.id || !id) return null

      const { data, error } = await supabase
        .from('reader_contents')
        .select('*')
        .eq('user_id', user.id)
        .eq('id', id)
        .maybeSingle()

      if (error) throw error
      return data ? (data as StoredReaderContent) : null
    },
    enabled: !!user?.id && !!id,
    staleTime: 1000 * 60 * 5
  })
}

export function useCreateReaderContent() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: ReaderContent & { fileSize?: number }) => {
      const content = { ...input }
      delete content.fileSize
      if (!user?.id) throw new Error('User not logged in')
      if (content.sourceType === 'story') {
        throw new Error('Stories are generated from curated content.')
      }

      const { data, error } = await supabase
        .from('reader_contents')
        .insert({
          user_id: user.id,
          source_type: content.sourceType,
          title: content.title,
          subtitle: content.subtitle ?? null,
          metadata: content.metadata ?? {},
          content,
          processor_version: content.processorVersion,
          dictionary_build_version: content.dictionaryBuildVersion ?? null
        })
        .select()
        .single()

      if (error) throw error
      return data as StoredReaderContent
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reader-contents', user?.id] })
      queryClient.invalidateQueries({
        queryKey: ['reader-content-summaries', user?.id]
      })
      toast.success('Reader item saved')
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'Could not save reader item'
      )
    }
  })
}

export function useUpdateReaderContent() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      content,
      progress
    }: {
      id: string
      content?: ReaderContent
      progress?: Record<string, unknown>
    }) => {
      if (!user?.id) throw new Error('User not logged in')

      const updates: Record<string, unknown> = {
        updated_at: new Date().toISOString()
      }

      if (content) {
        updates.title = content.title
        updates.subtitle = content.subtitle ?? null
        updates.metadata = content.metadata ?? {}
        updates.content = content
        updates.processor_version = content.processorVersion
        updates.dictionary_build_version =
          content.dictionaryBuildVersion ?? null
      }

      if (progress) {
        updates.progress = progress
        updates.last_opened_at = new Date().toISOString()
      }

      const { data, error } = await supabase
        .from('reader_contents')
        .update(updates)
        .eq('user_id', user.id)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as StoredReaderContent
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reader-contents', user?.id] })
      queryClient.invalidateQueries({
        queryKey: ['reader-content-summaries', user?.id]
      })
      queryClient.invalidateQueries({
        queryKey: ['reader-content', user?.id, variables.id]
      })
    }
  })
}

export function useDeleteReaderContent() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      if (!user?.id) throw new Error('User not logged in')

      const { error } = await supabase
        .from('reader_contents')
        .delete()
        .eq('user_id', user.id)
        .eq('id', id)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reader-contents', user?.id] })
      queryClient.invalidateQueries({
        queryKey: ['reader-content-summaries', user?.id]
      })
      toast.success('Reader item deleted')
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : 'Could not delete reader item'
      )
    }
  })
}
